import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link /* , Font */
} from '@react-pdf/renderer';
import MarkdownIt from 'markdown-it';
import PropTypes from 'prop-types';
import { Html } from 'react-pdf-html';

import { BUSINESS_IDEA_SECTIONS } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';

const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/zenbusiness/q_auto/v1/shared-assets/s2';
const ZENBUSINESS_LOGO_URL =
  'https://res.cloudinary.com/zenbusiness/image/upload/v1749845000/shared-assets/s2/logo/zb-velo-compact-color.png';
const ICON_BUSINESS_IDEAS_OVERVIEW_URL = `${CLOUDINARY_BASE_URL}/raster/`;

// Markdown parser (keep HTML disabled for safety; add plugins for GFM-like features)

const md = new MarkdownIt({
  html: false,
  breaks: true, // respect single line breaks
  linkify: true // auto-link URLs
});

const FOOTER_HEIGHT = 40;

const styles = StyleSheet.create({
  page: {
    padding: 32,
    paddingBottom: 32 + FOOTER_HEIGHT,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#111'
  },
  header: {
    marginBottom: 24,
    borderBottom: '1pt solid #ccc',
    paddingBottom: 12
  },
  subheader: {
    fontSize: 10,
    color: 'grey',
    textTransform: 'uppercase',
    marginBottom: 8
  },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 12 },
  subtitle: { fontSize: 12, color: '#444' },

  section: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1pt solid #eee',
    borderRadius: 4
  },
  sectionWhy: {
    backgroundColor: '#EAF2FF', // light blue
    borderRadius: 8,
    padding: '20px 24px 12px 24px', // padding bottom + 8px (<p> margin bottom)
    borderBottom: 0,
    marginBottom: 16
    // remove divider under this card
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 0
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#222'
  },
  sectionContent: {
    fontSize: 12,
    whiteSpace: 'pre-wrap'
  },
  sectionIcon: {
    width: 20,
    height: 20,
    marginRight: 6
  },
  footerImage: {
    width: '100%'
    // height: 60,
    // marginRight: 6
  },

  footerImageIcon: {
    width: 180,
    height: 12
    // marginRight: 6
  },
  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 24, // a little inset from page edge; change to 32 to align with padding
    height: FOOTER_HEIGHT,
    backgroundColor: '#fff' // keeps it clean if your viewer has a dark chrome
  },
  footerRule: {
    borderTop: '1pt solid #ddd',
    marginBottom: 6
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerLeft: {
    fontSize: 10,
    color: '#666'
  },
  footerRightImg: {
    width: 180, // your powered-by image width
    height: 15 // match the image’s aspect
  },
  // Optional: minimal CSS map for Html component (applies to nested HTML tags)
  htmlStyles: {
    // You can pass this object into <Html stylesheet={...} />
    'h1, h2, h3': { marginTop: 8, marginBottom: 6, fontWeight: 'bold' },
    h1: { fontSize: 18 },
    h2: { fontSize: 16 },
    h3: { fontSize: 14 },
    p: { marginBottom: 8 },
    ul: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    ol: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    li: { marginBottom: 4, listStyleType: 'disc' },
    a: { textDecoration: 'underline' },
    code: { fontSize: 11 },
    pre: {
      fontSize: 11,
      padding: 6,
      border: '1pt solid #ddd',
      borderRadius: 4,
      marginBottom: 8
    },
    blockquote: {
      borderLeft: '2pt solid #ccc',
      paddingLeft: 8,
      color: '#666',
      fontStyle: 'italic',
      marginBottom: 8
    },
    table: {
      width: '100%',
      border: '1pt solid #ddd',
      borderCollapse: 'collapse',
      marginBottom: 10
    },
    th: { padding: 4, fontWeight: 'bold', borderBottom: '1pt solid #ddd' },
    td: { padding: 4, borderBottom: '1pt solid #eee' },
    hr: { borderBottom: '1pt solid #ccc', marginTop: 8, marginBottom: 8 }
  }
});

// eslint-disable-next-line react/prop-types
function MarkdownPDFSection({ markdown }) {
  if (!markdown) return null;
  const html = md.render(markdown);
  return (
    <Html style={styles.sectionContent} stylesheet={styles.htmlStyles}>
      {html}
    </Html>
  );
}

export function BusinessIdeaPDF({ environment, idea }) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={ZENBUSINESS_LOGO_URL} style={{ width: 80, marginBottom: 20 }} />

        <View style={styles.header}>
          <Text style={styles.subheader}>Detailed business report</Text>
          <Text style={styles.title}>{idea.title}</Text>
          <Text style={styles.subtitle}>{idea.description}</Text>
        </View>
        {/* Sections */}
        {BUSINESS_IDEA_SECTIONS.map((s) => {
          const content = idea.businessPlan?.[s.id];
          const isWhySection = (id) =>
            id === 'whyThisWorksForYou' || id === 'why_this_works_for_you';
          if (!content) return null;
          return (
            <View key={s.id} style={isWhySection(s.id) ? styles.sectionWhy : styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                {s.icon && (
                  <Image
                    src={`${ICON_BUSINESS_IDEAS_OVERVIEW_URL}${s.icon}`}
                    style={styles.sectionIcon}
                  />
                )}
                <Text style={styles.sectionTitle}>{s.title}</Text>
              </View>

              <MarkdownPDFSection markdown={content} />
            </View>
          );
        })}

        <Link href={ROUTES.website(environment)} style={styles.footerImage} target="_blank">
          <Image
            src={`${CLOUDINARY_BASE_URL}/raster/business-ideas-banner.png`}
            style={styles.footerImage}
          />
        </Link>

        <View fixed style={styles.footer}>
          <View style={styles.footerRule} />

          <View style={styles.footerRow}>
            <Text style={styles.footerLeft}>support@zenbusiness.com</Text>
            <Image
              src={`${CLOUDINARY_BASE_URL}/raster/powered-by-zb-velo.png`}
              style={styles.footerRightImg}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

BusinessIdeaPDF.propTypes = {
  environment: PropTypes.string,
  idea: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    businessPlan: PropTypes.object
  }).isRequired
};

export default BusinessIdeaPDF;
